/**
 * Cat & Mouse — which letters a run drills (specs/cat-mouse-mode.md §8.2).
 *
 * A treat has no progress bar, but it must not deal random letters forever: a mode that reshuffles
 * the whole alphabet every run is a toy, not a drill. So this is a small private store — twenty
 * lines rather than a journey — keyed per language, next to the other per-device minigame records
 * in `game/storage.ts`.
 *
 * Nothing draws it. The gated window it produces is the same idea as `Pack.maxActiveDrillClasses`
 * in the Daily Life journey, implemented rather than inherited because a treat has no
 * `ReadingProgression` to hang it on.
 */
import { CAT } from '@/game/config';

const KEY = 'abcshooter.cat.letters.v1';

export interface LetterProgress {
  /** class id → runs it has appeared in. */
  seen: Record<string, number>;
  /** class id → boards cleared without losing a fish. */
  ok: Record<string, number>;
}

const EMPTY: LetterProgress = { seen: {}, ok: {} };

function read(): Record<string, LetterProgress> {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, LetterProgress>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // A quota error, a private window, a hand-edited value: the mode still plays, it just starts
    // the alphabet again. Never let a storage read end a run.
    return {};
  }
}

function write(all: Record<string, LetterProgress>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* see read() */
  }
}

export function letterProgress(lang: string): LetterProgress {
  const all = read();
  return all[lang] ?? EMPTY;
}

/**
 * Count one board cleared.
 *
 * `drilled` are the board's own classes: they were asked for, so they count toward *known*.
 * `alsoSeen` are the classes the board's decoys came from: the child looked straight at them while
 * deciding not to step on them, so they count as **met** but never as known. That distinction is
 * what lets the picture doors' alphabet (§7.2) grow at the speed the child actually meets letters,
 * instead of one or two per board — which left the ending of the mode undealable for four or five
 * runs, each of them paid for with a treat.
 */
export function recordBoard(
  lang: string, drilled: string[], clean: boolean, alsoSeen: string[] = [],
): void {
  const all = read();
  const cur = all[lang] ?? { seen: {}, ok: {} };
  const seen = { ...cur.seen };
  const ok = { ...cur.ok };
  for (const id of [...drilled, ...alsoSeen]) seen[id] = (seen[id] ?? 0) + 1;
  if (clean) for (const id of drilled) ok[id] = (ok[id] ?? 0) + 1;
  all[lang] = { seen, ok };
  write(all);
}

/** A class the child has cleared cleanly `CAT.knownAt` times. Known classes become decoys. */
export function isKnown(progress: LetterProgress, classId: string): boolean {
  return (progress.ok[classId] ?? 0) >= CAT.knownAt;
}

/**
 * The classes this run drills: the first `CAT.activeClasses` *unknown* ones in `rank` order.
 *
 * Mastering one frees its slot so the next locked letter comes in — a gated curriculum, not a
 * shuffle. When every class is known the window falls back to the whole alphabet, so a child who
 * has finished still gets a board rather than an empty one.
 */
export function activeClassIds(progress: LetterProgress, rankedIds: string[]): string[] {
  const unknown = rankedIds.filter((id) => !isKnown(progress, id));
  const window = unknown.slice(0, CAT.activeClasses);
  return window.length > 0 ? window : rankedIds.slice(0, CAT.activeClasses);
}

/** Every class the child has ever met — the picture doors' alphabet (§7.2). */
export function metClassIds(progress: LetterProgress, rankedIds: string[]): Set<string> {
  const met = rankedIds.filter((id) => (progress.seen[id] ?? 0) > 0);
  return new Set(met);
}
